import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useAdminAuth } from "@/components/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export default function AdminCreditProfitability() {
  const { admin } = useAdminAuth();
  const [profitability, setProfitability] = useState(null);
  const [marginBands, setMarginBands] = useState(null);
  const [riskUsers, setRiskUsers] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profitRes, bandsRes, riskRes] = await Promise.all([
          axios.get(`${API}/admin/credits/profitability`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/credits/margin-bands`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/credits/risk-users?limit=20`, { headers: getAuthHeaders() }),
        ]);
        setProfitability(profitRes.data);
        setMarginBands(bandsRes.data);
        setRiskUsers(riskRes.data);
      } catch (error) {
        console.error("Failed to fetch credit data:", error);
        toast.error("Failed to load credit profitability metrics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const MarginBadge = ({ band }) => {
    const colors = {
      healthy: "bg-green-100 text-green-800",
      tight: "bg-yellow-100 text-yellow-800",
      negative: "bg-red-100 text-red-800",
    };
    return (
      <Badge className={colors[band] || "bg-gray-100"}>
        {band}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-credit-profitability">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Credit Profitability</h1>
        <p className="text-muted-foreground">LLM costs and margin analysis</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Average Margin / Credit</CardDescription>
            <CardTitle className="text-3xl font-bold text-primary">
              ${profitability?.average_margin_per_credit?.toFixed(4) || '0.0000'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Across all users and plans
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Records Tracked</CardDescription>
            <CardTitle className="text-3xl font-bold">
              {profitability?.total_records || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              User-plan combinations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>At-Risk Users</CardDescription>
            <CardTitle className="text-3xl font-bold text-red-600">
              {riskUsers?.total_at_risk || 0}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Negative margin flag
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Margin by Plan */}
      <Card>
        <CardHeader>
          <CardTitle>Margin by Plan</CardTitle>
          <CardDescription>Average gross margin per credit by plan type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(profitability?.margin_by_plan || {}).map(([plan, margin]) => (
              <div key={plan} className="bg-muted rounded-lg p-4 text-center">
                <p className="text-xl font-bold">${margin?.toFixed(4) || '0.0000'}</p>
                <p className="text-sm text-muted-foreground capitalize">{plan}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* LLM Cost Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>LLM Cost by Model</CardTitle>
          <CardDescription>Total spending breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(profitability?.llm_cost_breakdown_by_model || {}).map(([model, cost]) => (
              <div key={model} className="flex items-center justify-between bg-muted rounded-lg px-4 py-3">
                <span className="font-medium text-foreground">{model}</span>
                <span className="font-bold text-primary">${cost?.toFixed(4) || '0.0000'}</span>
              </div>
            ))}
            {Object.keys(profitability?.llm_cost_breakdown_by_model || {}).length === 0 && (
              <p className="text-muted-foreground text-center py-4">No LLM usage data yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Margin Band Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Margin Band Distribution</CardTitle>
          <CardDescription>Users categorized by margin health</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-700">
                {marginBands?.margin_bands?.healthy?.count || 0}
              </p>
              <p className="text-sm text-green-600 font-medium">Healthy</p>
              <p className="text-xs text-muted-foreground mt-1">
                {'>'}50% margin
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">
                {marginBands?.margin_bands?.tight?.count || 0}
              </p>
              <p className="text-sm text-yellow-600 font-medium">Tight</p>
              <p className="text-xs text-muted-foreground mt-1">
                20-50% margin
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-red-700">
                {marginBands?.margin_bands?.negative?.count || 0}
              </p>
              <p className="text-sm text-red-600 font-medium">Negative</p>
              <p className="text-xs text-muted-foreground mt-1">
                {'<'}20% margin
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* High Cost Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>High Cost Users (Top 5%)</CardTitle>
          <CardDescription>Users with highest LLM spend</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User ID</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Analyses</TableHead>
                <TableHead>Total Cost</TableHead>
                <TableHead>Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(profitability?.high_cost_users || []).map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell className="font-mono text-xs">
                    {user.user_id?.slice(0, 8)}...
                  </TableCell>
                  <TableCell className="capitalize">{user.plan_type || 'unknown'}</TableCell>
                  <TableCell>{user.analyses_count}</TableCell>
                  <TableCell>${user.total_llm_cost?.toFixed(4)}</TableCell>
                  <TableCell>
                    <MarginBadge band={user.margin_band} />
                  </TableCell>
                </TableRow>
              ))}
              {(profitability?.high_cost_users || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                    No high-cost users yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
